/**
 * Company seed authoring - the single nested source both `tenant.seed.ts` and
 * `store.seed.ts` flatten from. A tenant is a retailer COMPANY; its physical
 * stores are authored inline underneath it (no repeated tenantId), which keeps
 * the company → stores relationship visible at authoring time.
 *
 * Business model: every company's products are ALWAYS on the global
 * marketplace (no opt-out - a deliberate deviation from HLD SM-08). A company
 * may additionally PAY for its own branded site - a subdomain-scoped
 * marketplace listing only its products - which is what `ownSiteEnabled`
 * gates. The subdomain itself is reserved for every company at onboarding.
 *
 * Derived downstream, never hand-maintained here:
 * - `stats.storeCount` / `stats.itemCount` / `stats.auctionsEndingToday`
 *   (tenant.seed.ts computes them from this roster + the listing seed)
 * - the "N locations · …" blurb prefix (from the nested store count)
 * - the tenant-band copy (from `name` + `subdomain` at render time)
 * - `hoursLabel` (store.seed.ts derives it from `weekHours` at seed time)
 *
 * Store coordinates are real-world so Haversine distances look right; phone
 * numbers use the reserved 555-01XX fictional range with real metro area
 * codes. Goodwill Industries of New Mexico is the exception: it is a real
 * pilot retailer, so its roster carries that company's published addresses,
 * hours and store numbers.
 */
import { StoreDayHours, StoreState, StoreWeekHours } from '@core/models/store.state';
import { TenantPolicyState, TenantThemeState } from '@core/models/tenant.state';

/** A store authored under its company - tenantId is stamped at flatten time. */
export interface CompanyStoreSeed extends Omit<StoreState, 'tenantId' | 'hoursLabel'> {
  /** Item-number prefix for listings enriched at this store, e.g. "GNM-SNM". */
  itemNoPrefix: string;
}

export interface CompanySeed {
  id: number;
  /** CompanyMgmt Company id (HLD §17 `MarketplaceTenant.CompanyId`); mirrors `id` in the mock. */
  companyId: number;
  slug: string;
  name: string;
  shortName: string;
  subdomain: string;
  /** The company pays for its own branded site at `{subdomain}.{baseDomain}`. */
  ownSiteEnabled: boolean;
  /** Blurb tail - tenant.seed.ts prefixes the derived "N locations · ". */
  blurbRest: string;
  theme: TenantThemeState;
  /** Shipping and returns terms shown before purchase (SHP-03, SM-06). */
  policy: TenantPolicyState;
  stores: CompanyStoreSeed[];
}

const day = (opens: string, closes: string): StoreDayHours => ({ opens, closes });

/** Thrift pattern: Mon-Sat 9 AM to `closes`, Sunday 10 AM to `sundayCloses`. */
const thriftWeek = (closes: string, sundayCloses: string): StoreWeekHours => [
  day('10:00', sundayCloses),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes)
];

/** ReStore pattern: closed Mondays, Tue-Sat 9 AM to `closes`, Sunday 10 AM-5 PM. */
const restoreWeek = (closes: string): StoreWeekHours => [
  day('10:00', '17:00'),
  null,
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes)
];

/** Boutique pattern: Mon-Fri 11 AM to `closes`, Saturday 10 AM to `closes`, Sunday noon-6 PM. */
const boutiqueWeek = (closes: string): StoreWeekHours => [
  day('12:00', '18:00'),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('10:00', closes)
];

/** Outlet pattern: open every day, 8 AM to 4:30 PM. */
const outletWeek = (): StoreWeekHours => Array.from({ length: 7 }, () => day('08:00', '16:30'));

export const seedCompanies = (): CompanySeed[] => [
  {
    id: 2,
    companyId: 2,
    slug: 'habitat-restore-la',
    name: 'Habitat ReStore LA',
    shortName: 'Habitat ReStore LA',
    subdomain: 'habitatla',
    ownSiteEnabled: true,
    blurbRest: 'Pasadena, Burbank, Norwalk, Van Nuys',
    policy: {
      handlingDays: 3,
      freeShippingThreshold: null,
      shippingSummary: 'Furniture and fixtures are pickup only. Smaller goods ship in 3 business days.',
      returnWindowDays: 14,
      returnSummary: 'Return anything except custom or clearance items within 14 days, in original condition.'
    },
    theme: {
      logoPrimaryText: 'habitat',
      logoSecondaryText: 'restore la',
      accent: '#3fa35b',
      accentDark: '#2b7d43',
      avatarBg: '#3fa35b',
      heroHeadline: 'Salvaged today. Someone’s home tomorrow.',
      heroCopy:
        'Doors, dressers, lighting, tile, and one-of-a-kind salvage from our four ReStores across greater LA - ' +
        'donated by builders and neighbors, priced to move, and ready for free store pickup.',
      searchPlaceholder: 'Search the ReStore - "solid oak table", "brass lamp", "french doors"…',
      missionText: 'Proceeds help Habitat for Humanity build affordable homes across Greater Los Angeles.',
      defaultLocation: { label: 'Los Angeles 90012', zip: '90012', lat: 34.0522, lng: -118.2437 }
    },
    stores: [
      {
        id: 21,
        slug: 'pasadena',
        name: 'Habitat ReStore LA - Pasadena',
        city: 'Pasadena',
        addressLine: '770 N Fair Oaks Ave',
        zip: '91103',
        lat: 34.1478,
        lng: -118.1445,
        phone: '(626) 555-0122',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 4 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.8,
        ratingCount: 529,
        itemNoPrefix: 'HRL-PAS'
      },
      {
        id: 22,
        slug: 'burbank',
        name: 'Habitat ReStore LA - Burbank',
        city: 'Burbank',
        addressLine: '1717 W Magnolia Blvd',
        zip: '91506',
        lat: 34.1808,
        lng: -118.309,
        phone: '(818) 555-0139',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 4 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.7,
        ratingCount: 344,
        itemNoPrefix: 'HRL-BUR'
      },
      {
        id: 23,
        slug: 'norwalk',
        name: 'Habitat ReStore LA - Norwalk',
        city: 'Norwalk',
        addressLine: '11900 Firestone Blvd',
        zip: '90650',
        lat: 33.9137,
        lng: -118.0764,
        phone: '(562) 555-0151',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: restoreWeek('19:00'),
        rating: 4.6,
        ratingCount: 402,
        itemNoPrefix: 'HRL-NOR'
      },
      {
        id: 24,
        slug: 'van-nuys',
        name: 'Habitat ReStore LA - Van Nuys',
        city: 'Van Nuys',
        addressLine: '7056 Van Nuys Blvd',
        zip: '91405',
        lat: 34.1975,
        lng: -118.4489,
        phone: '(818) 555-0164',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.5,
        ratingCount: 233,
        itemNoPrefix: 'HRL-VAN'
      }
    ]
  },
  {
    id: 4,
    companyId: 4,
    slug: 'matts-finds',
    name: "Matt's Finds",
    shortName: "Matt's Finds",
    subdomain: 'mattsfinds',
    ownSiteEnabled: true,
    blurbRest: 'Boston · Used hockey gear & collectibles',
    policy: {
      handlingDays: 1,
      freeShippingThreshold: 100,
      shippingSummary: 'Packed and shipped next business day, always tracked and insured.',
      returnWindowDays: 30,
      returnSummary:
        'Thirty-day returns on everything. Gear sizing is unpredictable and we would rather you skated in it.'
    },
    theme: {
      logoPrimaryText: "matt's",
      logoSecondaryText: 'finds',
      accent: '#235ba2',
      accentDark: '#0e3a5c',
      avatarBg: '#235ba2',
      heroHeadline: "Boston's rink-to-rack gear shop.",
      heroCopy:
        "Boston's home for used hockey gear, pro jerseys, and collectible cards. Bid on rare finds, " +
        'buy in one tap, and pick up free at any of our five stores - or have it delivered.',
      searchPlaceholder: 'Search Matt\'s - "Bauer skates", "rookie cards", "goalie pads"…',
      missionText:
        'A slice of every sale funds youth-hockey access programs that put donated gear on Boston kids who need it.',
      defaultLocation: { label: 'Boston 02116', zip: '02116', lat: 42.3503, lng: -71.081 }
    },
    stores: [
      {
        id: 41,
        slug: 'downtown-crossing',
        name: "Matt's Finds - Downtown Crossing",
        city: 'Boston',
        addressLine: '426 Washington St',
        zip: '02108',
        lat: 42.3555,
        lng: -71.0605,
        phone: '(617) 555-0116',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: boutiqueWeek('20:00'),
        rating: 4.9,
        ratingCount: 876,
        itemNoPrefix: 'MTF-BOS'
      },
      {
        id: 42,
        slug: 'cambridge',
        name: "Matt's Finds - Cambridge",
        city: 'Cambridge',
        addressLine: '1105 Massachusetts Ave',
        zip: '02138',
        lat: 42.3736,
        lng: -71.1097,
        phone: '(617) 555-0127',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: boutiqueWeek('20:00'),
        rating: 4.8,
        ratingCount: 542,
        itemNoPrefix: 'MTF-CAM'
      },
      {
        id: 43,
        slug: 'somerville',
        name: "Matt's Finds - Somerville",
        city: 'Somerville',
        addressLine: '240 Elm St, Davis Square',
        zip: '02144',
        lat: 42.3876,
        lng: -71.0995,
        phone: '(617) 555-0138',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.8,
        ratingCount: 431,
        itemNoPrefix: 'MTF-SOM'
      },
      {
        id: 44,
        slug: 'brookline',
        name: "Matt's Finds - Brookline",
        city: 'Brookline',
        addressLine: '318 Harvard St',
        zip: '02446',
        lat: 42.3318,
        lng: -71.1212,
        phone: '(617) 555-0149',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.7,
        ratingCount: 288,
        itemNoPrefix: 'MTF-BRK'
      },
      {
        id: 45,
        slug: 'quincy',
        name: "Matt's Finds - Quincy",
        city: 'Quincy',
        addressLine: '1469 Hancock St',
        zip: '02169',
        lat: 42.2529,
        lng: -71.0023,
        phone: '(617) 555-0157',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.6,
        ratingCount: 237,
        itemNoPrefix: 'MTF-QUI'
      }
    ]
  },
  {
    id: 7,
    companyId: 7,
    slug: 'goodwill-new-mexico',
    name: 'Goodwill Industries of New Mexico',
    shortName: 'Goodwill NM',
    subdomain: 'goodwillnm',
    ownSiteEnabled: true,
    blurbRest: 'Albuquerque, Santa Fe, Rio Rancho and across the state',
    policy: {
      handlingDays: 2,
      freeShippingThreshold: 75,
      shippingSummary: 'Ships in 2 business days from the Albuquerque processing center. Free over $75 statewide.',
      returnWindowDays: 7,
      returnSummary: 'Bring it back to any New Mexico store within 7 days with your receipt for a full refund.'
    },
    theme: {
      logoPrimaryText: 'goodwill',
      logoSecondaryText: 'nm',
      accent: '#0070b9',
      accentDark: '#005a95',
      avatarBg: '#0070b9',
      heroHeadline: 'One-of-a-kind means one chance.',
      heroCopy:
        'Furniture, vintage denim, housewares and collectibles cross our benches every day in twenty New Mexico ' +
        'stores. Buy it the moment we process it, then pick it up free where you already shop.',
      searchPlaceholder: 'Search Goodwill NM - "Pyrex", "vintage denim", "mid-century"…',
      missionText:
        'Every purchase funds job training, placement and support services for New Mexicans building careers.',
      defaultLocation: { label: 'Albuquerque 87102', zip: '87102', lat: 35.0844, lng: -106.6504 }
    },
    stores: [
      {
        id: 71,
        slug: 'goodthreads',
        name: 'Goodwill NM - GoodThreads',
        city: 'Albuquerque',
        addressLine: '3401 NM-528',
        zip: '87114',
        lat: 35.1997,
        lng: -106.6742,
        phone: '(505) 253-9950',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.8,
        ratingCount: 412,
        itemNoPrefix: 'GNM-GTH'
      },
      {
        id: 72,
        slug: 'outlet',
        name: 'Goodwill NM - Outlet',
        city: 'Albuquerque',
        addressLine: '3101 Menaul Blvd NE',
        zip: '87107',
        lat: 35.1063,
        lng: -106.6123,
        phone: '(505) 881-6566',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: outletWeek(),
        rating: 4.6,
        ratingCount: 938,
        itemNoPrefix: 'GNM-OUT'
      },
      {
        id: 73,
        slug: 'bluewater',
        name: 'Goodwill NM - Bluewater at 98th',
        city: 'Albuquerque',
        addressLine: '270 98th St NW',
        zip: '87121',
        lat: 35.0872,
        lng: -106.7688,
        phone: '(505) 314-1848',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.7,
        ratingCount: 526,
        itemNoPrefix: 'GNM-BLU'
      },
      {
        id: 74,
        slug: 'coors',
        name: 'Goodwill NM - Coors',
        city: 'Albuquerque',
        addressLine: '6636 Caminito Coors NW',
        zip: '87120',
        lat: 35.1503,
        lng: -106.6929,
        phone: '(505) 899-9790',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 604,
        itemNoPrefix: 'GNM-COO'
      },
      {
        id: 75,
        slug: 'juan-tabo',
        name: 'Goodwill NM - Juan Tabo at Copper',
        city: 'Albuquerque',
        addressLine: '700 Juan Tabo Blvd NE, Suite B',
        zip: '87123',
        lat: 35.0881,
        lng: -106.5013,
        phone: '(505) 881-6792',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.5,
        ratingCount: 389,
        itemNoPrefix: 'GNM-JTB'
      },
      {
        id: 76,
        slug: 'lomas',
        name: 'Goodwill NM - Lomas',
        city: 'Albuquerque',
        addressLine: '6001 Lomas Blvd NE, Suite R',
        zip: '87110',
        lat: 35.0893,
        lng: -106.5738,
        phone: '(505) 881-6751',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.7,
        ratingCount: 731,
        itemNoPrefix: 'GNM-LOM'
      },
      {
        id: 77,
        slug: 'menaul',
        name: 'Goodwill NM - Menaul',
        city: 'Albuquerque',
        addressLine: '11500 Menaul Blvd NE',
        zip: '87112',
        lat: 35.1057,
        lng: -106.5062,
        phone: '(505) 998-7860',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 558,
        itemNoPrefix: 'GNM-MEN'
      },
      {
        id: 78,
        slug: 'paseo',
        name: 'Goodwill NM - Paseo',
        city: 'Albuquerque',
        addressLine: '7120 Holly Ave NE',
        zip: '87113',
        lat: 35.1857,
        lng: -106.5652,
        phone: '(505) 998-2894',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.8,
        ratingCount: 642,
        itemNoPrefix: 'GNM-PAS'
      },
      {
        id: 79,
        slug: 'san-mateo',
        name: 'Goodwill NM - San Mateo',
        city: 'Albuquerque',
        addressLine: '5000 San Mateo Blvd NE',
        zip: '87109',
        lat: 35.1339,
        lng: -106.5838,
        phone: '(505) 881-6612',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.7,
        ratingCount: 1043,
        itemNoPrefix: 'GNM-SNM'
      },
      {
        id: 80,
        slug: 'tramway',
        name: 'Goodwill NM - Tramway',
        city: 'Albuquerque',
        addressLine: '12521 Montgomery Blvd NE',
        zip: '87111',
        lat: 35.1268,
        lng: -106.4952,
        phone: '(505) 314-1845',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 487,
        itemNoPrefix: 'GNM-TRM'
      },
      {
        id: 81,
        slug: 'renaissance',
        name: 'Goodwill NM - Renaissance',
        city: 'Albuquerque',
        addressLine: '1201 S Renaissance Blvd NE',
        zip: '87107',
        lat: 35.1524,
        lng: -106.6222,
        phone: '(505) 314-1920',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.5,
        ratingCount: 318,
        itemNoPrefix: 'GNM-REN'
      },
      {
        id: 82,
        slug: 'bernalillo',
        name: 'Goodwill NM - Bernalillo',
        city: 'Bernalillo',
        addressLine: '1021 Venada Plaza Dr',
        zip: '87004',
        lat: 35.3122,
        lng: -106.5531,
        phone: '(505) 404-2277',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.7,
        ratingCount: 263,
        itemNoPrefix: 'GNM-BER'
      },
      {
        id: 83,
        slug: 'rio-rancho',
        name: 'Goodwill NM - Rio Rancho',
        city: 'Rio Rancho',
        addressLine: '2003 Southern Blvd SE',
        zip: '87124',
        lat: 35.2233,
        lng: -106.6493,
        phone: '(505) 892-0215',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 511,
        itemNoPrefix: 'GNM-RRA'
      },
      {
        id: 84,
        slug: 'santa-fe',
        name: 'Goodwill NM - Santa Fe',
        city: 'Santa Fe',
        addressLine: '3060 Cerrillos Rd',
        zip: '87507',
        lat: 35.654,
        lng: -105.98,
        phone: '(505) 424-9726',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.8,
        ratingCount: 694,
        itemNoPrefix: 'GNM-SFE'
      },
      {
        id: 85,
        slug: 'los-lunas',
        name: 'Goodwill NM - Los Lunas',
        city: 'Los Lunas',
        addressLine: '201 Desert Willow Rd',
        zip: '87031',
        lat: 34.8135,
        lng: -106.722,
        phone: '(505) 944-0286',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.5,
        ratingCount: 224,
        itemNoPrefix: 'GNM-LSL'
      },
      {
        id: 86,
        slug: 'farmington',
        name: 'Goodwill NM - Farmington',
        city: 'Farmington',
        addressLine: '1861 N Hutton Rd',
        zip: '87402',
        lat: 36.7546,
        lng: -108.1683,
        phone: '(505) 326-6203',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 301,
        itemNoPrefix: 'GNM-FAR'
      },
      {
        id: 87,
        slug: 'gallup',
        name: 'Goodwill NM - Gallup',
        city: 'Gallup',
        addressLine: '1820 E Highway 66',
        zip: '87301',
        lat: 35.524,
        lng: -108.718,
        phone: '(505) 863-9217',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'GNM-GAL'
      },
      {
        id: 88,
        slug: 'clovis',
        name: 'Goodwill NM - Clovis',
        city: 'Clovis',
        addressLine: '2005 N Prince St',
        zip: '88101',
        lat: 34.4152,
        lng: -103.2043,
        phone: '(575) 762-7995',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.4,
        ratingCount: 187,
        itemNoPrefix: 'GNM-CLO'
      },
      {
        id: 89,
        slug: 'roswell',
        name: 'Goodwill NM - Roswell',
        city: 'Roswell',
        addressLine: '2601 N Main St',
        zip: '88201',
        lat: 33.411,
        lng: -104.5232,
        phone: '(575) 622-5500',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.5,
        ratingCount: 276,
        itemNoPrefix: 'GNM-ROS'
      },
      {
        id: 90,
        slug: 'carlsbad',
        name: 'Goodwill NM - Carlsbad',
        city: 'Carlsbad',
        addressLine: '1108 W Pierce St',
        zip: '88220',
        lat: 32.4103,
        lng: -104.2415,
        phone: '(575) 725-5311',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'GNM-CAR'
      }
    ]
  }
];
